// src/pages/Login.js
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { createPortal } from 'react-dom';

import { useAuth } from '../contexts/AuthContext';
import { db } from '../../../shared/storage/firebase';
import { FirebaseService } from '../../../shared/storage/FirebaseService';
import { hasFullPlatformStaffStatus, hasOnboardingHomeScheduleAccess } from '../teacherAccess';
import { StudentAuthService } from '../../../shared/storage/StudentAuthService';
import { User, Squad, StudentPublicProfile } from '../../../types';
import { OvertureLogo } from '../../../shared/assets/images/brand';
import StudentSelector from './StudentSelector';
import '../styles/Login.css';

type LoginMode = 'teacher' | 'student';
type StudentLoginView = 'form' | 'selector';

const CLAIM_SUB_REDIRECT_KEY = 'og_post_login_path';

interface SquadData {
  squad: Partial<Squad> & { onlineStudentIds?: string[] };
  students: StudentPublicProfile[];
}

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [pass, setPass] = useState<string>('');
  const [squadCode, setSquadCode] = useState<string>('');
  const [mode, setMode] = useState<LoginMode>('student');
  const [studentLoginView, setStudentLoginView] = useState<StudentLoginView>('form');
  const [squadData, setSquadData] = useState<SquadData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [studentSearchValue, setStudentSearchValue] = useState('');
  const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });
  const { setStudentSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Support admin "Launch" links (e.g. prefill parent email and open grown-up portal)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const prefillEmail = params.get('email') || '';
      const role = (params.get('role') || '').toLowerCase();
      const requestedMode = (params.get('mode') || '').toLowerCase();

      if (prefillEmail) setEmail(prefillEmail);
      if (requestedMode === 'teacher' || role === 'parent' || role === 'teacher' || role === 'partner') {
        setMode('teacher');
      }
    } catch (e) {
      // Ignore malformed query strings
    }
    // Only run on initial mount for stable behavior
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resetOpen) {
      setResetEmail((email || '').trim());
      setResetError(null);
      setResetSuccess(null);
    }
  }, [resetOpen, email]);

  const handleSendPasswordReset = async () => {
    const trimmed = resetEmail.trim();
    if (!trimmed) return;
    setResetLoading(true);
    setResetError(null);
    setResetSuccess(null);

    try {
      const functions = getFunctions();
      const sendReset = httpsCallable(functions, 'sendPasswordResetBrevo');
      await sendReset({ email: trimmed, continueUrl: `${window.location.origin}/auth/action` });
      setResetSuccess('Reset email sent. Check your inbox (and spam) for a secure link.');
    } catch (e: any) {
      console.error('Password reset error:', e);
      setResetError('If an account exists for that email, you’ll receive a reset link shortly.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleTooltipShow = (e: React.MouseEvent, content: string) => {
    setTooltip({
      visible: true,
      content,
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleTooltipMove = (e: React.MouseEvent) => {
    if (tooltip.visible) {
      setTooltip(t => ({ ...t, x: e.clientX, y: e.clientY }));
    }
  };

  const handleTooltipHide = () => {
    setTooltip(t => ({ ...t, visible: false }));
  };

  const handleTeacherSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const auth = getAuth();
      const user = await FirebaseService.signInUser(email, pass);

      if (user) {
        const userData = await FirebaseService.getUserData(user.uid);
        if (!userData) {
          setError('Account not found.');
          await auth.signOut();
          return;
        }

        // Teachers go to squads; parents go to parent dashboard/signup flow later
        if (userData.role === 'teacher') {
          const staffStatus = userData.staffStatus ?? 'active';
          const onboardingInProgress = ['offer_sent', 'offer_accepted', 'onboarding']
            .includes(String((userData as any).hiringStatus || ''));
          if (userData.isAdmin !== true && (!hasFullPlatformStaffStatus(staffStatus) || onboardingInProgress)) {
            navigate(hasOnboardingHomeScheduleAccess(userData) ? '/app/squads' : '/app/onboarding');
            return;
          }
          // If a Slack claim link was opened pre-login, honor it once.
          try {
            const next = sessionStorage.getItem(CLAIM_SUB_REDIRECT_KEY);
            if (next) {
              sessionStorage.removeItem(CLAIM_SUB_REDIRECT_KEY);
              navigate(next);
              return;
            }
          } catch (_) {
            // ignore storage errors
          }

          // Admin default landing: Admin panel → Users → Partner Seasons
          if (userData.isAdmin === true) {
            navigate('/app/admin', { state: { adminTab: 'users', adminUserTab: 'partners' } });
            return;
          }

          navigate('/app/squads');
          return;
        }

        if (userData.role === 'parent') {
          navigate('/app/students');
          return;
        }

        if (userData.role === 'partner') {
          // Track partner logins (best-effort). Rules restrict partners to only update these fields.
          try {
            await updateDoc(doc(db, 'users', user.uid), {
              loginCount: increment(1),
              lastLogin: serverTimestamp(),
            } as any);
          } catch (e) {
            console.warn('Failed to update partner login stats:', e);
          }
          navigate('/app/games');
          return;
        }

        setError('Access denied. Only teachers, partners, or parents can use the Grown-Up Portal.');
        await auth.signOut();
      }
    } catch (error: any) {
      console.error('Teacher login error:', error);
      setError(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // First, authenticate anonymously to get Firestore read permissions
      const auth = getAuth();
      await signInAnonymously(auth);

      // Now we can query Firestore directly
      const squadsRef = collection(db, 'squads');
      const q = query(squadsRef, where('code', '==', squadCode));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('Squad not found. Please check your code.');
        return;
      }

      const squadDoc = querySnapshot.docs[0];
      const squadData = squadDoc.data() as Squad;

      // Fetch student profiles
      const studentProfiles: StudentPublicProfile[] = [];
      if (squadData.studentIds && squadData.studentIds.length > 0) {
        for (const studentId of squadData.studentIds) {
          const studentDoc = await getDoc(doc(db, 'users', studentId));
          if (studentDoc.exists()) {
            const studentData = studentDoc.data() as User;
            studentProfiles.push({
              id: studentDoc.id,
              displayName: studentData.displayName || 'Unnamed Student',
              selectedMuse: studentData.selectedMuse || null,
            });
          }
        }
      }

      setSquadData({
        squad: {
          id: squadDoc.id,
          title: squadData.title,
          onlineStudentIds: squadData.onlineStudentIds || [],
        },
        students: studentProfiles,
      });
      setStudentLoginView('selector');
    } catch (error: any) {
      console.error('Error fetching squad:', error);
      setError(error.message || 'Failed to find squad. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStudent = async (studentId: string) => {
    const student = squadData?.students.find(s => s.id === studentId);
    if (!student || !squadData?.squad.id) {
      setError('Student or squad data is missing.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { firebaseUser, studentData } = await StudentAuthService.authenticateStudent(studentId, squadData.squad.id);
      
      console.log(`Student ${student.displayName} (${studentId}) authenticated successfully!`);

      // Use the new direct method to set the session in the AuthContext
      setStudentSession(studentData, firebaseUser);
      
      navigate('/app/games');
    } catch (error: any) {
      console.error('Student login error:', error);
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCode = () => {
    setStudentLoginView('form');
    setSquadData(null);
    setSquadCode('');
    setError(null);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPass(e.target.value);
  };

  const handleSquadCodeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSquadCode(e.target.value.toUpperCase());
  };

  // Registration from this page has been removed intentionally.

  return (
    <>
      {tooltip.visible && createPortal(
        <div
          className="custom-tooltip"
          style={{
            top: tooltip.y + 20,
            left: tooltip.x,
          }}
        >
          {tooltip.content}
        </div>,
        document.body
      )}

      {resetOpen && createPortal(
        <div className="reset-modal-overlay" role="dialog" aria-modal="true">
          <div className="reset-modal-card">
            <div className="reset-modal-title">Reset your password</div>
            <div className="reset-modal-subtitle">
              We’ll email you a secure link to set a new password.
            </div>

            <div className="reset-modal-form">
              <label className="reset-modal-label">Email address</label>
              <input
                className="reset-modal-input"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
              {resetError && <div className="reset-modal-error">{resetError}</div>}
              {resetSuccess && <div className="reset-modal-success">{resetSuccess}</div>}
            </div>

            <div className="reset-modal-actions">
              <button
                type="button"
                className="reset-modal-primary"
                onClick={handleSendPasswordReset}
                disabled={resetLoading || !resetEmail.trim()}
              >
                {resetLoading ? 'Sending…' : 'Send reset email'}
              </button>
              <button
                type="button"
                className="reset-modal-secondary"
                onClick={() => setResetOpen(false)}
                disabled={resetLoading}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {studentLoginView === 'selector' && squadData ? (
        <>
          <div className="student-search-container">
            <input
              type="text"
              placeholder="Search for your name..."
              className="student-search-input"
              value={studentSearchValue}
              onChange={(e) => setStudentSearchValue(e.target.value)}
            />
          </div>
          <StudentSelector
            students={squadData.students}
            onlineStudentIds={squadData.squad.onlineStudentIds || []}
            squadName={squadData.squad.title || 'Your Squad'}
            onSelectStudent={handleSelectStudent}
            onBack={handleBackToCode}
            loading={loading}
            searchValue={studentSearchValue}
          />
        </>
      ) : (
        <div className="login-container">
          <div className="login-content">
            {/* Epic Logo Section */}
            <div className="logo-section">
              <img src={OvertureLogo} alt="Overture Logo" className="epic-logo" />
            </div>

            {/* New Demo CTA Button */}
            <a href="/demo" className="demo-cta-button">
              <span role="img" aria-label="gamepad icon" className="gamepad-icon"></span>
              Play Free Demo Games
            </a>            
            <div className="login-card">
              {/* Mode Switcher */}
              <div className="login-mode-switcher">
                <button 
                  onClick={() => setMode('student')} 
                  className={`login-mode-btn ${mode === 'student' ? 'active' : ''}`}
                >
                  Student Login
                </button>
                <button 
                  onClick={() => setMode('teacher')} 
                  className={`login-mode-btn ${mode === 'teacher' ? 'active' : ''}`}
                  onMouseEnter={(e) => handleTooltipShow(e, 'For teachers, administrators and guardians')}
                  onMouseMove={handleTooltipMove}
                  onMouseLeave={handleTooltipHide}
                >
                  Grown-Up Portal
                </button>
              </div>

              {/* Card Content */}
              <div className="login-card-content">
                {error && <div className="error-banner">{error}</div>}
 
                              {mode === 'teacher' ? (
                  <>
                    <h1 className="login-title">
                       Grown-Up Portal
                    </h1>
                    <form onSubmit={handleTeacherSubmit} className="login-form">
                      <div className="input-container">
                        <input
                          className="login-input"
                          placeholder="Email Address"
                          value={email}
                          onChange={handleEmailChange}
                          type="email"
                          required
                          aria-label="Email"
                        />
                        <span className="input-icon">📧</span>
                      </div>
                      <div className="input-container">
                        <input
                          className="login-input"
                          placeholder="Password"
                          type="password"
                          value={pass}
                          onChange={handlePasswordChange}
                          required
                          aria-label="Password"
                        />
                        <span className="input-icon">🔒</span>
                      </div>
                      <div className="login-forgot-row">
                        <button
                          type="button"
                          className="login-forgot-button"
                          onClick={() => setResetOpen(true)}
                        >
                          Forgot password?
                        </button>
                      </div>
                      <button type="submit" className="login-button" disabled={loading}>
                        {loading ? 'Signing In...' : 'Sign In'}
                      </button>
                    </form>
                  </>
                ) : (
                  <>
                    <h1 className="login-title">
                      Join Your Squad
                    </h1>
                    <form onSubmit={handleStudentSubmit} className="login-form">
                      <div className="input-container">
                        <input
                          className="login-input"
                          placeholder="SQUAD CODE"
                          value={squadCode}
                          onChange={handleSquadCodeChange}
                          type="text"
                          required
                          maxLength={5}
                          aria-label="Squad Code"
                        />
                        <span className="input-icon">🎯</span>
                      </div>
                      <button type="submit" className="login-button student" disabled={loading}>
                        {loading ? 'Finding Squad...' : 'Find My Squad'}
                      </button>
                    </form>
                  </>
                               )}
                
                {loading && (
                  <div className="login-loading-overlay">
                    <div className="loading-spinner"></div>
                  </div>
                )}
              </div>
            </div>
            </div>
          
        </div>
      )}
    </>
  );
};

export default Login;
